import { isEqual } from 'radash';
import {
  resolveFindingCvssVersionRecord,
  resolveFindingSelectedCvssVersion,
} from '@terra/shared-cvss';
import type { EditableFields } from '@terra/ui-design-system';

export function needsFindingHydration({
  editableFields,
  findingTitle,
}: {
  editableFields: EditableFields;
  findingTitle?: string | null;
}): boolean {
  const hasFindingTitle = Boolean(findingTitle?.trim());
  const hasEditableTitle = Boolean(editableFields.title?.trim());

  return hasFindingTitle && !hasEditableTitle;
}

export function needsServerOwnedOwaspRiskHydration({
  editableFields,
  originalFields,
  findingOwaspRisk,
}: {
  editableFields: EditableFields;
  originalFields: EditableFields | null;
  findingOwaspRisk: EditableFields['owaspRisk'];
}): boolean {
  if (!findingOwaspRisk?.level) {
    return false;
  }

  const localOwaspRisk = editableFields.owaspRisk ?? null;
  const originalOwaspRisk = originalFields?.owaspRisk ?? null;
  const owaspRiskNotDirty = isEqual(localOwaspRisk, originalOwaspRisk);

  return owaspRiskNotDirty && !isEqual(localOwaspRisk, findingOwaspRisk);
}

export function mergeHydratedFindingFields({
  current,
  findingFields,
}: {
  current: EditableFields;
  findingFields: EditableFields;
}): EditableFields {
  const pickString = (
    currentValue: string | null | undefined,
    findingValue: string | null | undefined,
  ) => {
    const currentText = (currentValue ?? '').trim();

    return currentText !== '' ? (currentValue ?? '') : (findingValue ?? '');
  };

  return {
    ...findingFields,
    title: pickString(current.title, findingFields.title),
    shortDescription: pickString(
      current.shortDescription,
      findingFields.shortDescription,
    ),
    description: pickString(current.description, findingFields.description),
    poc: pickString(current.poc, findingFields.poc),
    businessImpact: pickString(
      current.businessImpact,
      findingFields.businessImpact,
    ),
    mitigationPlan: pickString(
      current.mitigationPlan,
      findingFields.mitigationPlan,
    ),
    assignedTo: pickString(current.assignedTo, findingFields.assignedTo),
    findingUpdateComment: pickString(
      current.findingUpdateComment,
      findingFields.findingUpdateComment,
    ),
    status:
      current.status !== 'Unresolved' ? current.status : findingFields.status,
    severity:
      current.severity !== 'Low' ? current.severity : findingFields.severity,
    category: current.category ?? findingFields.category ?? null,
    pocBlocks: current.pocBlocks ?? findingFields.pocBlocks ?? null,
    signalId: current.signalId ?? findingFields.signalId ?? null,
    traceThreadId: current.traceThreadId ?? findingFields.traceThreadId ?? null,
    assets: current.assets.length > 0 ? current.assets : findingFields.assets,
    cvss: current.cvss ?? findingFields.cvss ?? null,
    cvss40: current.cvss40 ?? findingFields.cvss40 ?? null,
    cvss31: current.cvss31 ?? findingFields.cvss31 ?? null,
    owaspRisk: current.owaspRisk ?? findingFields.owaspRisk ?? null,
    isPublished: current.isPublished ?? findingFields.isPublished ?? null,
  };
}

export function buildEditableFieldsFromFinding({
  finding,
}: {
  finding: {
    title?: string | null;
    shortDescription?: string | null;
    description?: string | null;
    poc?: string | null;
    pocBlocks?: EditableFields['pocBlocks'];
    businessImpact?: string | null;
    mitigationPlan?: string | null;
    status?: string | null;
    severity?: string | null;
    category?: EditableFields['category'];
    assignedTo?: string | null;
    rawAssets?: EditableFields['assets'];
    assets?: Array<{
      type: EditableFields['assets'][number]['type'];
      id: string;
    }>;
    signal?: { id: string } | null;
    signalId?: string | null;
    traceThreadId?: string | null;
    cvss?: EditableFields['cvss'];
    cvss40?: EditableFields['cvss'];
    cvss31?: EditableFields['cvss'];
    cvssVersion?: string | null;
    owaspRisk?: EditableFields['owaspRisk'];
    isPublished?: boolean | null;
    findingUpdateComment?: string | null;
  };
}): EditableFields {
  const assetsToUse =
    finding.rawAssets && finding.rawAssets.length > 0
      ? finding.rawAssets
      : finding.assets?.map((asset) => ({
          type: asset.type,
          id: asset.id,
        })) || [];
  const versionSource = {
    cvss: finding.cvss,
    cvss40: finding.cvss40,
    cvss31: finding.cvss31,
    cvssVersion: finding.cvssVersion,
  };
  const selectedRecord = resolveFindingCvssVersionRecord({
    finding: versionSource,
    version: resolveFindingSelectedCvssVersion(versionSource),
  });

  return {
    title: finding.title || '',
    shortDescription: finding.shortDescription ?? '',
    description: finding.description ?? '',
    poc: finding.poc ?? '',
    pocBlocks: finding.pocBlocks ?? null,
    businessImpact: finding.businessImpact ?? '',
    mitigationPlan: finding.mitigationPlan ?? '',
    status: (finding.status ?? 'Unresolved') as EditableFields['status'],
    severity: (finding.severity ?? 'Low') as EditableFields['severity'],
    category: finding.category || null,
    assignedTo: finding.assignedTo || '',
    assets: assetsToUse,
    signalId: finding.signal?.id ?? finding.signalId ?? null,
    traceThreadId: finding.traceThreadId || null,
    cvss: finding.cvss || selectedRecord || null,
    cvss40: finding.cvss40 ?? null,
    cvss31: finding.cvss31 ?? null,
    owaspRisk: finding.owaspRisk || null,
    isPublished: finding.isPublished ?? null,
    findingUpdateComment: finding.findingUpdateComment ?? null,
  };
}
